/**
 * Client for the local-CC channel plugin.
 *
 * POSTs to a TCP port on 127.0.0.1 exposed by the channel plugin running
 * inside the pueue-managed `claude` process. Returns the raw text Claude
 * wrote into the `reply` tool's `result` argument.
 */

import { request as httpRequest } from 'node:http';
import { connect } from 'node:net';
import type { LocalCCRole } from './prompts.js';
import { appendTurn } from './turnLog.js';

export const CHANNEL_HOST = '127.0.0.1';
export const CHANNEL_PORT = parseInt(process.env.SYNKRO_CHANNEL_PORT || '8929', 10);

const DEFAULT_TIMEOUT_MS = 90_000;

export class LocalCCError extends Error {
  constructor(message: string, public readonly cause?: unknown) {
    super(message);
    this.name = 'LocalCCError';
  }
}

export interface SubmitOptions {
  /** Timeout for the round-trip in ms (default 90s). */
  timeoutMs?: number;
  /** Override the channel port (default: CHANNEL_PORT / 8929). */
  port?: number;
}

/**
 * Send a request through the channel and wait for Claude's `reply` tool output.
 * Throws LocalCCError if the channel isn't reachable, the channel times out, or
 * the plugin returns a non-2xx status.
 */
export async function submitToChannel(
  role: LocalCCRole,
  payload: string,
  opts: SubmitOptions = {},
): Promise<string> {
  const body = JSON.stringify({ role, payload, content: payload });
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const port = opts.port ?? CHANNEL_PORT;
  const startedAt = Date.now();

  try {
    const result = await new Promise<string>((resolve, reject) => {
      const req = httpRequest({
        host: CHANNEL_HOST,
        port,
        method: 'POST',
        path: '/submit',
        headers: {
          'Content-Type': 'application/json',
          'Content-Length': Buffer.byteLength(body),
        },
        timeout: timeoutMs,
      }, res => {
        const chunks: Buffer[] = [];
        res.on('data', c => chunks.push(c));
        res.on('end', () => {
          const text = Buffer.concat(chunks).toString('utf-8');
          if (res.statusCode !== 200) {
            reject(new LocalCCError(`channel returned ${res.statusCode}: ${text.slice(0, 500)}`));
            return;
          }
          try {
            const parsed = JSON.parse(text) as { result?: string; error?: string };
            if (parsed.error) {
              reject(new LocalCCError(parsed.error));
              return;
            }
            resolve(String(parsed.result ?? ''));
          } catch (err) {
            reject(new LocalCCError(`malformed channel response: ${text.slice(0, 200)}`, err));
          }
        });
      });
      req.on('timeout', () => {
        req.destroy(new LocalCCError(`channel request timed out after ${timeoutMs}ms`));
      });
      req.on('error', err => {
        const msg = (err as NodeJS.ErrnoException).code === 'ECONNREFUSED'
          ? `channel connection refused at ${CHANNEL_HOST}:${CHANNEL_PORT} (is the pueue task running?)`
          : `channel request failed: ${(err as Error).message}`;
        reject(new LocalCCError(msg, err));
      });
      req.write(body);
      req.end();
    });
    appendTurn({ startedAt, role, request: payload, result, status: 'ok' });
    return result;
  } catch (err) {
    const message = (err as Error).message ?? String(err);
    const status = /timed out/i.test(message) ? 'timeout' : 'error';
    appendTurn({ startedAt, role, request: payload, status, error: message });
    throw err;
  }
}

/** Quick TCP probe — true if anything is listening on the given port (default: CHANNEL_PORT). */
export function isChannelAvailable(port = CHANNEL_PORT, timeoutMs = 500): Promise<boolean> {
  return new Promise(resolve => {
    const sock = connect(port, CHANNEL_HOST);
    const done = (ok: boolean) => {
      try { sock.destroy(); } catch { /* noop */ }
      resolve(ok);
    };
    sock.once('connect', () => done(true));
    sock.once('error', () => done(false));
    sock.setTimeout(timeoutMs, () => done(false));
  });
}
